import { useMemo } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { useQuery } from "@tanstack/react-query";
import { startOfMonth } from "date-fns";

import { PublicHolidayResponse, publicHolidaysApi } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { AppointmentTargetCalculatorFormValues } from "../AppointmentTargetCalculatorDialog.types";
import {
  DEFAULT_1B_TARGET_PER_WEEK,
  DEFAULT_EG_TARGET_PER_WEEK,
  FULL_TIME_DAYS_PER_WEEK,
  MANUAL_MONTH_ID,
  buildMonthData,
  calculateTargets,
  usesManualCalendarWorkdays,
} from "../AppointmentTargetCalculatorDialog.utils";

// Stable reference so the `data = …` fallback below doesn't allocate a new array each render,
// which would re-run the buildMonthData memo on every render while the query is loading.
const NO_HOLIDAYS: PublicHolidayResponse[] = [];

export function useAppointmentTargetCalculator() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  // A timestamp rather than a Date, so the month-data memo below re-runs only when the month changes.
  const currentMonthStart = startOfMonth(new Date()).getTime();

  // Same query key as the holiday-import settings view, so the cached holiday list is reused.
  const { data: holidays = NO_HOLIDAYS } = useQuery({
    queryKey: [QueryKey.PublicHolidays],
    queryFn: () => publicHolidaysApi.getPublicHolidays(),
  });

  const formik = useFormikWithoutDirtyForm<AppointmentTargetCalculatorFormValues>({
    initialValues: {
      // Default to the full-time 5-day week.
      workdays: FULL_TIME_DAYS_PER_WEEK,
      weeklyHours: null,
      month: MANUAL_MONTH_ID,
      calendarWorkdays: null,
      vacationDays: 0,
      trainingDays: 0,
      otherAbsenceDays: 0,
      actualWorkdays: null,
      target1bPerWeek: DEFAULT_1B_TARGET_PER_WEEK,
      targetEgPerWeek: DEFAULT_EG_TARGET_PER_WEEK,
    },
    onSubmit: () => {
      // Pure calculator — the result is computed live, nothing is persisted.
    },
  });

  const { options: monthOptions, workdaysById } = useMemo(
    () => buildMonthData(t, language, new Date(currentMonthStart), holidays),
    [t, language, currentMonthStart, holidays]
  );

  // A real month feeds its pre-computed calendar working days straight into the calculation;
  // the manual sentinel falls back to the hand-entered calendarWorkdays field.
  const result = useMemo(() => {
    const { month, calendarWorkdays } = formik.values;
    const effectiveCalendarWorkdays = usesManualCalendarWorkdays(month)
      ? calendarWorkdays
      : (workdaysById.get(month ?? "") ?? null);
    return calculateTargets({ ...formik.values, calendarWorkdays: effectiveCalendarWorkdays });
  }, [formik.values, workdaysById]);

  return { formik, result, monthOptions };
}
